import { Message, create, fromBinary, toBinary } from '@bufbuild/protobuf';
import { AnySchema } from '@bufbuild/protobuf/wkt';
import { Client } from '@connectrpc/connect';

import CancelToken from './cancelToken';
import { InvalidChannelMessageError } from './errorsClient';
import {
  LoginHashCommitmentBodySchema,
  LoginClientPubKeyBodySchema,
  LoginAuthenticatorPubKeyBodySchema,
  LoginEncryptedSecretBodySchema,
} from './espb/channel_messages_pb';
import { ChannelService } from './espb/channel_service_pb';
import { requireFieldNotEmpty } from './grpcUtils';
import { toSyncDto } from './sync';
import {
  AuthenticatorId,
  ChannelId,
  ChannelRecipient,
  ChannelType,
  SyncUpdate,
  ClientPubKeyDto,
  EncryptedSecretDto,
  HashCommitmentDto,
  LoginChannelId,
  UserId,
} from './types';

export default class ChannelServiceClient {
  constructor(private readonly espbClient: Client<typeof ChannelService>) {}

  async create(
    userId: UserId,
    data: Uint8Array,
    type: ChannelType,
    exposed: boolean,
  ): Promise<ChannelId> {
    const resp = await this.espbClient.create({
      userId,
      data,
      channelType: type,
      exposed,
    });
    return requireFieldNotEmpty(resp.channelId);
  }

  async claim<T extends Message>(
    deserialize: (bytes: Uint8Array) => T,
    channelId: ChannelId,
    authenticatorId: AuthenticatorId,
    data: Uint8Array,
  ): Promise<T> {
    const resp = await this.espbClient.claim({ channelId, authenticatorId, data });

    try {
      return deserialize(resp.data);
    } catch {
      throw new InvalidChannelMessageError();
    }
  }

  async read<T extends Message>(
    deserialize: (bytes: Uint8Array) => T,
    channelId: ChannelId,
    recipient: ChannelRecipient,
    cancelToken?: CancelToken,
  ): Promise<T> {
    const resp = await this.espbClient.read(
      { channelId, recipient },
      { signal: cancelToken?.signal },
    );

    try {
      return deserialize(resp.data);
    } catch {
      throw new InvalidChannelMessageError();
    }
  }

  async write(channelId: ChannelId, recipient: ChannelRecipient, data: Uint8Array): Promise<void> {
    await this.espbClient.write({ channelId, data, recipient });
  }

  async delete(channelId: ChannelId): Promise<{
    // empty if method is called without user authentication
    syncUpdate?: SyncUpdate;
  }> {
    const resp = await this.espbClient.delete({ channelId });
    const respSyncUpdate = resp.syncUpdate;
    if (respSyncUpdate) {
      const syncUpdate = toSyncDto(respSyncUpdate);
      return { syncUpdate };
    }
    return {};
  }

  async loginChannelInitialize(
    userId: UserId,
    hashCommitment: Uint8Array,
  ): Promise<LoginChannelId> {
    const message = create(LoginHashCommitmentBodySchema, { hashCommitment });
    return this.create(userId, toBinary(LoginHashCommitmentBodySchema, message), 'login', true);
  }

  async loginChannelClose(channelId: LoginChannelId): Promise<SyncUpdate | undefined> {
    return this.delete(channelId).then((resp) => resp.syncUpdate);
  }

  async loginChannelSendClientPubKey(
    pushChannelId: LoginChannelId,
    clientPubKey: Uint8Array,
  ): Promise<void> {
    const message = create(LoginClientPubKeyBodySchema, { clientPubKey });
    await this.write(
      pushChannelId,
      ChannelRecipient.AUTHENTICATOR,
      toBinary(LoginClientPubKeyBodySchema, message),
    );
  }

  async loginChannelReceiveAuthenticatorPubKey(
    pushChannelId: LoginChannelId,
    cancelToken?: CancelToken,
  ): Promise<Uint8Array> {
    const message = await this.read(
      (b) => fromBinary(LoginAuthenticatorPubKeyBodySchema, b),
      pushChannelId,
      ChannelRecipient.CLIENT,
      cancelToken,
    );

    return requireFieldNotEmpty(message.authenticatorPubKey);
  }

  async loginChannelReceiveEncryptedSecret(
    pushChannelId: LoginChannelId,
    cancelToken?: CancelToken,
  ): Promise<EncryptedSecretDto> {
    const message = await this.read(
      (b) => fromBinary(LoginEncryptedSecretBodySchema, b),
      pushChannelId,
      ChannelRecipient.CLIENT,
      cancelToken,
    );

    return {
      authenticatorId: requireFieldNotEmpty(message.authenticatorId),
      encryptedSecret: requireFieldNotEmpty(message.encryptedSecret),
    };
  }

  async loginChannelSendEncryptedSecret(
    pushChannelId: LoginChannelId,
    encryptedSecret: Uint8Array,
    authenticatorId: AuthenticatorId,
  ): Promise<void> {
    const message = create(LoginEncryptedSecretBodySchema, { encryptedSecret, authenticatorId });
    await this.write(
      pushChannelId,
      ChannelRecipient.CLIENT,
      toBinary(LoginEncryptedSecretBodySchema, message),
    );
  }

  async loginChannelClaimAndReceiveHashCommitment(
    pushChannelId: LoginChannelId,
    authenticatorId: AuthenticatorId,
    authenticatorPubKey: Uint8Array,
  ): Promise<HashCommitmentDto> {
    const authPubKeyMessage = create(LoginAuthenticatorPubKeyBodySchema, { authenticatorPubKey });

    const hashCommitmentMessage = await this.claim(
      (b) => fromBinary(LoginHashCommitmentBodySchema, b),
      pushChannelId,
      authenticatorId,
      toBinary(LoginAuthenticatorPubKeyBodySchema, authPubKeyMessage),
    );

    return requireFieldNotEmpty(hashCommitmentMessage.hashCommitment);
  }

  async loginChannelReceiveClientPubKey(pushChannelId: LoginChannelId): Promise<ClientPubKeyDto> {
    const message = await this.read(
      (b) => fromBinary(LoginClientPubKeyBodySchema, b),
      pushChannelId,
      ChannelRecipient.AUTHENTICATOR,
    );

    return requireFieldNotEmpty(message.clientPubKey);
  }

  async loginChannelWaitUntilClosed(pushChannelId: LoginChannelId): Promise<void> {
    try {
      await this.read(
        (b) => fromBinary(AnySchema, b),
        pushChannelId,
        ChannelRecipient.AUTHENTICATOR,
      );
    } catch {
      // channel closed now or already closed, return ok
    }
  }
}
